import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaTrash,
  FaPlus,
  FaEdit,
  FaGlobe,
  FaLock,
  FaSave,
  FaCopy,
  FaCheck,
  FaThumbsUp,
  FaThumbsDown,
  FaGamepad,
  FaRegCalendarAlt,
  FaUserCircle,
  FaSlidersH,
  FaSearch,
  FaEyeSlash,
  FaChevronLeft,
  FaCrown,
} from "react-icons/fa";
import DragAndDropImage from "../../components/DragAndDropImage";
import QuestionEditor from "../../components/customMode/QuestionEditor";
import QuestionDetail from "../../components/customMode/QuestionDetail";
import { getPointColor } from "../../utils/pointColor";
import {
  getCustomModeById,
  updateMode,
  deleteMode,
  publishMode,
  unpublishMode,
  removeModeFromUser,
  likeMode,
  dislikeMode,
  addModeById,
} from "../../api/custom-mode";
import { useSubscription } from "../../contexts/SubscriptionContext";
import CommunityExtras from "../../components/community/CommunityExtras";
import SubscriptionModal from "../../components/subscription/SubscriptionModal";
import {
  Button,
  buttonClass,
  Card,
  Field,
  Input,
  Modal,
  PageSpinner,
  Spinner,
  Textarea,
} from "../../ui";

const POINTS = [100, 200, 300, 400, 500];

const ModeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPremium } = useSubscription();
  const [subOpen, setSubOpen] = useState(false);

  const [name, setName] = useState(null);
  const [info, setInfo] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [editingMeta, setEditingMeta] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editorQ, setEditorQ] = useState(null); // "new" | question
  const [detailQ, setDetailQ] = useState(null);
  const [confirmDeleteMode, setConfirmDeleteMode] = useState(false);

  // question list filters
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [pointFilter, setPointFilter] = useState([]);
  const [sortBy, setSortBy] = useState("newest");

  // like state
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);

  const { data: mode, isLoading } = useQuery({
    queryKey: ["mode", id],
    queryFn: () => getCustomModeById(id),
  });

  useEffect(() => {
    if (mode && name === null) {
      setName(mode.name || "");
      setInfo(mode.info || "");
      setIsLiked(!!mode.isLiked);
      setIsDisliked(!!mode.isDisliked);
    }
  }, [mode, name]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["mode", id] });
    queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
  };

  const saveMeta = useMutation({
    mutationFn: () => updateMode(id, { name: name.trim(), info: info.trim(), imageFile }),
    onSuccess: () => {
      invalidate();
      setImageFile(null);
      setEditingMeta(false);
      toast.success("تم حفظ التغييرات");
    },
    onError: (e) => toast.error(e?.response?.data?.message || "تعذّر الحفظ"),
  });
  const togglePublish = useMutation({
    mutationFn: () => (mode.locked ? publishMode(id) : unpublishMode(id)),
    onSuccess: () => {
      invalidate();
      toast.success(mode.locked ? "تم نشر الفئة" : "تم إلغاء النشر");
    },
    onError: () => toast.error("تعذّر تغيير حالة النشر"),
  });
  const removeMode = useMutation({
    mutationFn: () => deleteMode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
      toast.success("تم حذف الفئة");
      navigate("/app/profile");
    },
    onError: () => toast.error("تعذّر حذف الفئة"),
  });
  const removeFromLib = useMutation({
    mutationFn: () => removeModeFromUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
      toast.success("تمت إزالة الفئة");
      navigate("/app/community");
    },
    onError: () => toast.error("تعذّرت الإزالة"),
  });
  // The state to reach is sent (not a toggle): safe to retry, and on current
  // servers liking clears a dislike in the same request.
  const likeMut = useMutation({ mutationFn: (liked) => likeMode(id, liked) });
  const dislikeMut = useMutation({
    mutationFn: (disliked) => dislikeMode(id, disliked),
  });
  const addMut = useMutation({
    mutationFn: () => addModeById(id),
    onSuccess: () => {
      invalidate();
      toast.success("تمت إضافة الفئة");
    },
    onError: (e) => toast.error(e?.response?.data?.message || "تعذّرت الإضافة"),
  });

  const questions = useMemo(() => mode?.questions || [], [mode]);
  const filteredQuestions = useMemo(() => {
    let list = [...questions];
    if (search.trim())
      list = list.filter((q) => (q.question || "").includes(search.trim()));
    if (pointFilter.length)
      list = list.filter((q) => pointFilter.includes(q.points));
    const byDate = (a, b) =>
      new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    if (sortBy === "newest") list.sort(byDate);
    else if (sortBy === "oldest") list.sort((a, b) => -byDate(a, b));
    else if (sortBy === "pointsHigh") list.sort((a, b) => b.points - a.points);
    else if (sortBy === "pointsLow") list.sort((a, b) => a.points - b.points);
    return list;
  }, [questions, search, pointFilter, sortBy]);

  if (isLoading || name === null) {
    return <PageSpinner />;
  }
  if (!mode) {
    return <p className="text-white/70 text-center py-20">الفئة غير موجودة</p>;
  }

  const isOwner = mode.isOwner;
  // Backend populates the owner as `ownerInfo`; fall back to an object `owner`.
  const ownerObj =
    mode.ownerInfo || (typeof mode.owner === "object" ? mode.owner : null);
  const ownerId =
    ownerObj?._id || (typeof mode.owner === "string" ? mode.owner : null);
  const ownerName = ownerObj?.gameName || ownerObj?.name || "";
  const questionCount = mode.questionsCount ?? questions.length;
  // Mirror the app: owners + premium see all questions; a free non-owner sees
  // only the first as a preview, the rest locked behind the subscription wall.
  const canSeeAll = isOwner || isPremium;
  const visibleQuestions = canSeeAll
    ? filteredQuestions
    : filteredQuestions.slice(0, 1);
  const hiddenCount = canSeeAll ? 0 : Math.max(0, questionCount - 1);
  const createdDate = mode.createdAt
    ? new Date(mode.createdAt).toLocaleDateString("ar", { numberingSystem: "latn" })
    : null;

  const copyCode = () => {
    if (!mode.invitationCode) return;
    navigator.clipboard?.writeText(mode.invitationCode);
    setCopied(true);
    toast.success("تم نسخ الكود");
    setTimeout(() => setCopied(false), 2000);
  };
  const handleLike = () => {
    const next = !isLiked;
    setIsLiked(next);
    if (next && isDisliked) {
      setIsDisliked(false);
      // Older servers don't clear a dislike when liking.
      dislikeMut.mutate(false);
    }
    likeMut.mutate(next);
  };
  const handleDislike = () => {
    const next = !isDisliked;
    setIsDisliked(next);
    if (next && isLiked) setIsLiked(false);
    dislikeMut.mutate(next);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="-mr-2 p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="رجوع"
        >
          <FaArrowRight />
        </button>
        <h1 className="text-2xl md:text-3xl font-extrabold">مصنع الفئات</h1>
      </div>

      {/* Owner header (non-owner only) */}
      {!isOwner && ownerId && (
        <button
          onClick={() => navigate(`/app/users/${ownerId}`)}
          className="w-full rounded-2xl border border-white/10 bg-white/[0.05] hover:border-white/20 p-3 mb-4 flex items-center gap-3 transition-colors"
        >
          {ownerObj?.image ? (
            <img
              src={ownerObj.image}
              alt=""
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <FaUserCircle className="w-10 h-10 text-white/30" />
          )}
          <div className="text-right">
            <p className="text-white/55 text-xs">صاحب الفئة</p>
            <p className="text-white font-semibold">{ownerName}</p>
          </div>
        </button>
      )}

      {/* Mode card */}
      <Card className="mb-4">
        {editingMeta ? (
          <div className="space-y-4">
            <Field label="صورة الفئة">
              <DragAndDropImage
                onFileSelected={setImageFile}
                initialPreview={mode.image || ""}
              />
            </Field>
            <Field label="اسم الفئة" htmlFor="mode-name">
              <Input
                id="mode-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="اسم الفئة"
              />
            </Field>
            <Field label="الوصف" htmlFor="mode-info" hint={`${info.length}/400`}>
              <Textarea
                id="mode-info"
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                rows={2}
                maxLength={400}
                className="resize-none"
                placeholder="الوصف"
              />
            </Field>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => saveMeta.mutate()}
                disabled={saveMeta.isPending}
                icon={saveMeta.isPending ? <Spinner /> : <FaSave />}
              >
                حفظ
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setEditingMeta(false)}
              >
                إلغاء
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-4">
              {mode.image && (
                <img
                  src={mode.image}
                  alt={mode.name}
                  className="w-24 h-24 rounded-xl object-contain bg-white/[0.06] shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-white text-xl font-extrabold mb-2">
                  {mode.name}
                </h2>
                {mode.invitationCode && (
                  <button
                    onClick={copyCode}
                    className="h-9 px-3 rounded-lg border border-white/15 bg-white/[0.07] hover:bg-white/10 text-white text-sm font-bold inline-flex items-center gap-2 mb-2 transition-colors"
                    aria-label="نسخ كود الدعوة"
                  >
                    {copied ? (
                      <FaCheck className="text-success" />
                    ) : (
                      <FaCopy className="text-primary" />
                    )}
                    <bdi dir="ltr">{mode.invitationCode}</bdi>
                  </button>
                )}
                <p className="text-white/70 text-sm flex items-center gap-1.5">
                  <FaGamepad className="text-mainButton" />{" "}
                  {mode.playedInGamesCount || 0}
                </p>
                {createdDate && (
                  <p className="text-white/55 text-xs flex items-center gap-1.5 mt-1">
                    <FaRegCalendarAlt /> {createdDate}
                  </p>
                )}
              </div>
            </div>

            {/* like / dislike */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
              <button
                onClick={handleLike}
                disabled={isOwner}
                className="h-9 px-3 rounded-lg flex items-center gap-1.5 text-white font-bold hover:bg-white/10 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                aria-label="إعجاب"
                aria-pressed={isLiked}
              >
                <FaThumbsUp className={isLiked ? "text-success" : ""} />{" "}
                {(mode.likesCount || 0) + (isLiked && !mode.isLiked ? 1 : 0)}
              </button>
              <div className="w-px h-5 bg-white/10" />
              <button
                onClick={handleDislike}
                disabled={isOwner}
                className="h-9 px-3 rounded-lg flex items-center gap-1.5 text-white font-bold hover:bg-white/10 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                aria-label="عدم إعجاب"
                aria-pressed={isDisliked}
              >
                <FaThumbsDown className={isDisliked ? "text-danger" : ""} />{" "}
                {(mode.dislikesCount || 0) +
                  (isDisliked && !mode.isDisliked ? 1 : 0)}
              </button>

              {!isOwner && (
                <Button
                  size="sm"
                  variant={mode.isAdded ? "secondary" : "primary"}
                  className="me-auto"
                  onClick={() =>
                    mode.isAdded ? removeFromLib.mutate() : addMut.mutate()
                  }
                  icon={<FaPlus />}
                >
                  {mode.isAdded ? "إزالة" : "إضافة"}
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Owner controls */}
      {isOwner && !editingMeta && (
        <Card className="mb-4 space-y-4">
          <button
            onClick={() => setConfirmPublish(true)}
            disabled={togglePublish.isPending}
            className={`w-full flex items-center justify-between gap-3 rounded-xl border p-3.5 transition-colors disabled:opacity-60 ${
              mode.locked
                ? "bg-primary/10 border-primary/30 hover:bg-primary/15"
                : "bg-success/10 border-success/30 hover:bg-success/15"
            }`}
          >
            <div className="flex items-center gap-3">
              <span
                className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${
                  mode.locked ? "bg-primary/15 text-primary" : "bg-success/15 text-success"
                }`}
              >
                {mode.locked ? <FaLock /> : <FaGlobe />}
              </span>
              <div className="text-right">
                <p className="text-white font-bold">
                  {mode.locked ? "فئة خاصة" : "فئة عامة"}
                </p>
                <p className="text-white/55 text-xs">
                  {mode.locked ? "اضغط لنشرها للجميع" : "متاحة في فئات المجتمع"}
                </p>
              </div>
            </div>
            <FaChevronLeft className="text-white/40 shrink-0" aria-hidden="true" />
          </button>

          <div className="border-t border-white/10 pt-4 flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setEditingMeta(true)}
              icon={<FaEdit />}
            >
              تعديل
            </Button>
            <button
              onClick={() => setConfirmDeleteMode(true)}
              className="h-11 w-11 shrink-0 rounded-xl bg-danger/15 hover:bg-danger/25 text-danger flex items-center justify-center transition-colors"
              aria-label="حذف الفئة"
            >
              <FaTrash />
            </button>
          </div>
        </Card>
      )}

      {/* Questions */}
      <div className="flex items-center justify-between gap-3 mt-8 mb-3">
        <h2 className="text-lg font-bold">
          الأسئلة ({questionCount})
        </h2>
        {isOwner && (
          <Button size="sm" onClick={() => setEditorQ("new")} icon={<FaPlus />}>
            سؤال جديد
          </Button>
        )}
      </div>

      {canSeeAll && questions.length > 0 && (
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setShowFilter((v) => !v)}
            className={`w-12 h-12 shrink-0 rounded-xl border flex items-center justify-center text-white transition-colors ${
              showFilter
                ? "bg-mainButton border-mainButton"
                : "bg-white/[0.07] border-white/15 hover:border-white/25 hover:bg-white/10"
            }`}
            aria-label="تصفية"
            aria-expanded={showFilter}
          >
            <FaSlidersH />
          </button>
          <div className="relative flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث..."
              aria-label="ابحث في الأسئلة"
              className="pr-11"
            />
            <FaSearch
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      {showFilter && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 mb-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {POINTS.map((p) => (
              <button
                key={p}
                onClick={() =>
                  setPointFilter((f) =>
                    f.includes(p) ? f.filter((x) => x !== p) : [...f, p]
                  )
                }
                className={`text-xs font-bold rounded-full h-8 px-3 transition-colors ${
                  pointFilter.includes(p)
                    ? "text-white"
                    : "bg-white/10 text-white/70 hover:text-white"
                }`}
                style={
                  pointFilter.includes(p)
                    ? { backgroundColor: getPointColor(p) }
                    : undefined
                }
                aria-pressed={pointFilter.includes(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "newest", l: "الأحدث" },
              { v: "oldest", l: "الأقدم" },
              { v: "pointsHigh", l: "الأعلى نقاط" },
              { v: "pointsLow", l: "الأقل نقاط" },
            ].map((s) => (
              <button
                key={s.v}
                onClick={() => setSortBy(s.v)}
                className={`text-xs font-bold h-8 px-3 rounded-full transition-colors ${
                  sortBy === s.v
                    ? "bg-primary text-header"
                    : "bg-white/10 text-white/70 hover:text-white"
                }`}
                aria-pressed={sortBy === s.v}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {questionCount === 0 ? (
          <p className="text-white/55 text-center py-8 rounded-2xl border border-dashed border-white/15">
            لا توجد أسئلة
          </p>
        ) : (
          visibleQuestions.map((q) => (
            <button
              key={q._id}
              onClick={() => setDetailQ(q)}
              className="relative w-full text-right rounded-2xl border border-white/10 bg-white/[0.05] hover:border-white/20 p-4 pt-5 flex items-start gap-3 transition-colors"
            >
              <span
                className="absolute -top-2.5 right-4 text-white text-xs font-bold rounded-full px-2.5 py-0.5 shadow"
                style={{ backgroundColor: getPointColor(q.points) }}
              >
                {q.points}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white/55 text-[11px] font-semibold mb-1">السؤال</p>
                <div className="bg-white/[0.06] rounded-lg p-2.5 mb-2">
                  <p className="text-white text-sm leading-relaxed line-clamp-2">
                    {q.question}
                  </p>
                </div>
                <p className="text-white/55 text-[11px] font-semibold mb-1">الإجابة</p>
                <div className="bg-white/[0.04] rounded-lg p-2.5">
                  {isOwner ? (
                    <p className="text-white/80 text-xs line-clamp-1">
                      {q.answer}
                    </p>
                  ) : (
                    <div className="flex justify-center text-white/40">
                      <FaEyeSlash size={13} />
                    </div>
                  )}
                </div>
              </div>
              <FaChevronLeft className="text-white/30 mt-2 shrink-0" />
            </button>
          ))
        )}

        {!canSeeAll && hiddenCount > 0 && (
          <button
            onClick={() => setSubOpen(true)}
            className="w-full rounded-2xl p-5 border border-primary/30 bg-primary/[0.08] hover:bg-primary/[0.12] flex flex-col items-center gap-3 transition-colors"
          >
            <div className="flex items-center gap-2 text-primary">
              <FaLock />
              <span className="font-bold">+{hiddenCount} سؤال مخفي</span>
            </div>
            <span className={buttonClass({ size: "sm" })}>
              <FaCrown /> إظهار جميع الأسئلة
            </span>
          </button>
        )}
      </div>

      {editorQ && (
        <QuestionEditor
          modeId={id}
          question={editorQ === "new" ? null : editorQ}
          onClose={() => setEditorQ(null)}
          onSaved={() => {
            setEditorQ(null);
            invalidate();
          }}
        />
      )}

      {detailQ && (
        <QuestionDetail
          question={detailQ}
          isOwner={isOwner}
          onEdit={() => {
            const q = detailQ;
            setDetailQ(null);
            setEditorQ(q);
          }}
          onClose={() => setDetailQ(null)}
        />
      )}

      <SubscriptionModal open={subOpen} onClose={() => setSubOpen(false)} />

      <Modal
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        title={mode.locked ? "نشر الفئة" : "إلغاء النشر"}
        size="sm"
      >
        <p className="text-white/70 leading-relaxed mb-6">
          {mode.locked
            ? "ستصبح الفئة عامة ومتاحة لجميع اللاعبين في فئات المجتمع."
            : "ستصبح الفئة خاصة ولن تظهر في فئات المجتمع."}
        </p>
        <div className="flex gap-3">
          <Button
            className="flex-1"
            onClick={() => {
              togglePublish.mutate();
              setConfirmPublish(false);
            }}
            disabled={togglePublish.isPending}
          >
            {mode.locked ? "نشر" : "إلغاء النشر"}
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => setConfirmPublish(false)}
          >
            إلغاء
          </Button>
        </div>
      </Modal>

      <Modal
        open={confirmDeleteMode}
        onClose={() => !removeMode.isPending && setConfirmDeleteMode(false)}
        title="حذف الفئة"
        size="sm"
      >
        <p className="text-white/70 leading-relaxed mb-6">
          هل تريد حذف "{mode.name}" وكل أسئلتها؟ لا يمكن التراجع.
        </p>
        <div className="flex gap-3">
          <Button
            variant="danger"
            className="flex-1"
            onClick={() => removeMode.mutate()}
            disabled={removeMode.isPending}
            icon={removeMode.isPending ? <Spinner /> : <FaTrash />}
          >
            حذف
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => setConfirmDeleteMode(false)}
          >
            إلغاء
          </Button>
        </div>
      </Modal>
      {!isOwner && <CommunityExtras modeId={id} />}
    </div>
  );
};

export default ModeEdit;
